import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {v4 as uuid} from 'uuid';
import {EIconSmallButton, ETooltip} from 'webapp-lib/Components';
import {usePopup} from 'webapp-lib/Functions';
import {escapeRegularExpression} from 'webapp-lib/utils';

import List from '#/List';
import ListItem, {Props as ListItemProps} from '#/List/Item';
import Loading from '#/Loading';
import SearchBar from '#/SearchBar';
import {ChevronSmallDown, Close, HorizontalDots, Info} from '_/assets/images';
import {usePromise} from '_/hooks';
import {I18NextNS} from '_/i18n';
import {memo} from '_/utilities/memo';
import {
  PaginationArguments,
  PaginationDirection,
  PaginationMode,
  PaginationResponse,
  Positioning,
  State,
} from '_/utilities/types';

import Action from '../../InputAction';
import {
  BottomSection,
  InfoIconWrapper,
  Label,
  LeftAssistiveText,
  TopSection,
  Wrapper,
} from '../../base-input-styles';
import {BaseInputProps} from '../../base-input-types';

import {
  Dropdown,
  Header,
  HeaderLabel,
  IconWrapper,
  InputHeader,
  StartingPoint,
  StyledClearButtonWrapper,
  StyledSelectBox,
  StyledSelectBoxItem,
  StyledSelectBoxItemText,
  StyledSelectBoxItemTitle,
} from './styles';

const LIST_SIZE_LIMIT = 200;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export interface Item {
  section?: string | number;
  id?: string;
  cursor?: string;
  text: string;
  /** If a title is passed, it will show above the actual text in a small font size. */
  title?: string;
  callback?: () => void;
  inactive?: boolean;
  inactiveStyle?: boolean;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /**
   * If this property is true the item is flagged as user input and
   * not treated the same as the other items fetched from the source or handed-down via prop
   */
  isCustomItem?: boolean;
}

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export type Props<T extends Item = Item> = Omit<
  React.SelectHTMLAttributes<HTMLDivElement>,
  'onChange'
> &
  BaseInputProps & {
    DropdownPlaceholderImage?: React.FunctionComponent<
      React.SVGProps<SVGSVGElement>
    >;
    Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    baseReference?: React.RefObject<HTMLElement>;
    dropdownPlaceholderHeader?: string;
    expanded?: boolean;
    frameElement?: HTMLElement | null;
    hasMoreAfter?: boolean;
    hasMoreBefore?: boolean;
    headerText?: string;
    headerTitle?: string;
    isLoading?: boolean;
    initialSearchQuery?: string;
    items?: T[];
    ItemComponent?: React.FunctionComponent<ListItemProps<T>>;
    hidePlaceholderWhenQueryIsEmpty?: boolean;
    suggestedItems?: T[];
    noHeader?: boolean;
    placeholder?: string;
    placement?: Positioning;
    ref?: React.Ref<HTMLDivElement>;
    /**
     * naturalDropdownWidth allows the overriding of the fixed width of a dropdown,
     * with this prop the dropdown takes the width of this child elements
     */
    naturalDropdownWidth?: boolean;
    /**
     * When this prop is set, the dropdown will jump to the active item as it is its default behavior
     * but will not focus this element. (Especially useful if the input field should not loose focus)
     */
    nonFocusActiveItem?: boolean;
    /**
     * listProps are a extendable list of props that will be handed down to the list component
     */
    listProps?: {style?: {[key: string]: string}};
    /**
     * Whether to show the search icon in the search bar or not
     */

    /**
     * disableSelect disables the selectability via the keyboard
     */
    disableSelect?: boolean;
    showSearchIcon?: boolean;
    searchQuery?: string;
    searchable?: true;
    sectionComponents?: Record<string | number, React.FunctionComponent<any>>;
    selectedItem?: T;
    source?: (arg0: PaginationArguments & {query?: string}) => {
      promise: Promise<PaginationResponse<T>>;
      abort: () => void;
    };
    startingPoint: StartingPoint;
    /**
     * @param {string} changedValue - the value after the change happend
     * @param {?boolean} committed - nullable boolean to indicate if change was committed to instance value
     * @param {?T} item - the selected item if a item was selected
     */
    onChange?: (changeValue: string, committed?: boolean, item?: T) => void;
    /**
     * Will add a cross icon and call this function onClick
     */
    onClear?: () => void;
    /**
     * onExpandedChange/expanded control expansion, disables internal handling of expansion state
     */
    onExpandedChange?: (arg0: boolean) => void;
    /**
     * Unlike "onBlur" it won't be called when the base element loses focus to any sub-elements of the select box, it
     * will be triggered only when the focus is lost from select box and an element out of the select box gained the
     * focus. It is mostly useful to know when user clicks out of the select box, etc.
     */
    onFocusLose?: () => void;
    onItemChange?: (arg0: T) => void;
    onLoadMoreAfter?: () => void;
    onLoadMoreBefore?: () => void;
    onSearchQueryChange?: (value: string) => void;
    /**
     * Will be called when user hits enter after entering a text in the search bar. Providing this value will disable
     * the default "select first item when nothing is selected" behavior so that user has the option to hit enter and
     * activates her custom value instead of any of the items in the list. In other words providing this prop means it
     * should be an editable select box.
     */
    onSearchQuerySubmit?: (value: string) => void;
    value?: string;
    testId?: string;
    rightAligned?: boolean;
    allowInputAsItem?: boolean;
    /**
     * Avoid filtering the items locally if no source is present and the items are searchable
     * this is an oversight as the original creator of the stored component, they did not think of the implications
     * this had for instances that handle the fetching of elements themselves.
     * So this variable is mainly used to avoid any further filtering if the elements are handled by the "instance".
     */
    disallowLocalFiltering?: boolean;
  };

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
const SelectBoxItem = memo(
  <T extends Item = Item>(
    {item, ...props}: ListItemProps<T>,
    ref: React.Ref<HTMLDivElement>
  ): React.ReactElement =>
    item.title ? (
      <StyledSelectBoxItem ref={ref} item={item} title={item.text} {...props}>
        <StyledSelectBoxItemTitle>{item.title}</StyledSelectBoxItemTitle>
        <StyledSelectBoxItemText>{item.text}</StyledSelectBoxItemText>
      </StyledSelectBoxItem>
    ) : (
      <ListItem ref={ref} item={item} title={item.text} {...props}>
        {item.text}
      </ListItem>
    ),
  true
);

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 * When removing this function, remove {@link usePromise} as well
 */
// eslint-disable-next-line max-statements,complexity
export function SelectBoxComponent<T extends Item = Item>(
  {
    Icon: StartIcon_,
    DropdownPlaceholderImage,
    actions = [],
    disallowLocalFiltering,
    baseReference,
    dropdownPlaceholderHeader,
    error = false,
    expanded: externalExpandedState,
    frameElement,
    headerText,
    items: items_,
    initialSearchQuery = '',
    ItemComponent = SelectBoxItem,
    hidePlaceholderWhenQueryIsEmpty: hidePlaceholderWhenQueryIsEmpty_,
    suggestedItems,
    label,
    leftAssistiveText,
    noHeader,
    placeholder = '',
    placement = Positioning.TopRightAlignDown,
    showSearchIcon = true,
    searchQuery: searchQuery_,
    searchable,
    selectedItem: selectedItem_,
    disableSelect = false,
    source,
    startingPoint,
    success = false,
    tabIndex,
    title,
    value,
    testId,
    onBlur,
    onChange,
    onClick,
    onExpandedChange,
    onFocusLose,
    onItemChange,
    onKeyDown,
    onKeyPress,
    onLoadMoreAfter,
    onLoadMoreBefore,
    onSearchQueryChange,
    onSearchQuerySubmit,
    onClear,
    isLoading,
    hasMoreAfter: hasMoreAfter_,
    hasMoreBefore: hasMoreBefore_,
    naturalDropdownWidth = false,
    nonFocusActiveItem = false,
    listProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rightAligned = false,
    allowInputAsItem,
    ...props
  }: Props<T>,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement {
  let StartIcon = StartIcon_;

  if (!StartIcon) {
    if (startingPoint === StartingPoint.Icon) {
      StartIcon = HorizontalDots;
    } else {
      StartIcon = ChevronSmallDown;
    }
  }
  const [expanded_, setExpanded_] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(initialSearchQuery);
  const hidePlaceholderWhenQueryIsEmpty =
    hidePlaceholderWhenQueryIsEmpty_ === undefined
      ? Boolean(suggestedItems)
      : hidePlaceholderWhenQueryIsEmpty_;
  const searchQuery = searchQuery_ == null ? localSearchValue : searchQuery_;
  const [internalItems, setItems] = useState<T[]>([]);
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const clearIconRef = useRef<HTMLButtonElement>(null);
  const labelId = useMemo(uuid, []);
  const items = items_ || internalItems;

  const [listRef, setListRef] = useState<HTMLDivElement | null>(null);
  const [suggestionsListRef, setSuggestionsListRef] =
    useState<HTMLDivElement | null>(null);
  const [internalRef, setInternalRef] = useState<HTMLDivElement | null>(null);

  const [internalHasMoreAfter, setHasMoreAfter] = useState(false);
  const [internalHasMoreBefore, setHasMoreBefore] = useState(false);

  const {t} = useTranslation<I18NextNS>('common');

  const filteredItems = useMemo(() => {
    const regularExpression = new RegExp(
      escapeRegularExpression(searchQuery),
      'ui'
    );

    if (allowInputAsItem && searchQuery !== '') {
      const itemsWithCustom = [
        ...items,
        {
          id: searchQuery,
          text: `${t('selectbox.entity.customAddress')}: <${searchQuery}>`,
          isCustomItem: true,
        } as T,
      ];
      return source || disallowLocalFiltering
        ? itemsWithCustom
        : itemsWithCustom.filter(({text}) => regularExpression.test(text));
    }
    return source || disallowLocalFiltering
      ? items
      : items.filter(({text}) => regularExpression.test(text));
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchQuery, disallowLocalFiltering, t]);

  const lastActiveSuggestedItem = useMemo(
    () =>
      suggestedItems
        ?.slice()
        .reverse()
        .find(({inactive}) => !inactive),
    [suggestedItems]
  );

  const selectedItem = useMemo(
    () =>
      selectedItem_ ||
      items.find((item) => item.id === value) ||
      suggestedItems?.find((item) => item.id === value),
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, value, selectedItem_]
  );

  const firstActiveItem = useMemo(
    () =>
      onSearchQuerySubmit
        ? undefined
        : filteredItems.find((item) => item === selectedItem) ||
          filteredItems.find(({inactive}) => !inactive),
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredItems, selectedItem]
  );

  const [selection, setSelection] = useState<{
    currentSection: 'main' | 'search' | 'suggestions';
    mainItems: T[];
    suggestions: T[];
  }>({
    currentSection: 'search',
    mainItems: firstActiveItem && searchable ? [firstActiveItem] : [],
    suggestions: lastActiveSuggestedItem ? [lastActiveSuggestedItem] : [],
  });

  const hasMoreAfter =
    hasMoreAfter_ == null ? internalHasMoreAfter : hasMoreAfter_;
  const hasMoreBefore =
    hasMoreBefore_ == null ? internalHasMoreBefore : hasMoreBefore_;

  const expanded =
    externalExpandedState == null ? expanded_ : externalExpandedState;
  const setExpanded = onExpandedChange || setExpanded_;

  const focusSearchBar = useCallback(() => {
    setSelection((currentState) => ({
      ...currentState,
      currentSection: 'search',
    }));
    searchRef.current?.focus();
  }, []);

  const toggleSelectMenu = useCallback(() => {
    if (props.disabled) {
      return;
    }
    setExpanded(!expanded);
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, props.disabled]);

  useEffect(() => {
    if (expanded) {
      if (searchRef.current) {
        focusSearchBar();
      } else {
        listRef?.focus();
      }
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, listRef]);

  useEffect(() => {
    /* istanbul ignore if */
    if (ref) {
      if (typeof ref === 'function') {
        /* istanbul ignore next */
        ref(internalRef);
      } else {
        // (ref as React.MutableRefObject<HTMLDivElement | null>).current = internalRef;
      }
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalRef]);

  useEffect(() => {
    function keyPressed({key}: KeyboardEvent): void {
      if (key === 'Escape') {
        setExpanded(false);
      }
    }

    window.addEventListener('keydown', keyPressed);

    return (): void => {
      window.removeEventListener('keydown', keyPressed);
    };
  }, [setExpanded]);

  const abortSignal = useRef<(() => void) | undefined>(undefined);

  const {status: itemsStatus, update: updateItems} = usePromise(() => {
    if (!source) {
      return {};
    }
    abortSignal.current?.();
    let aborted = false;

    setItems([]);
    const {abort, promise} =
      (value || selectedItem_?.id) && !searchQuery
        ? source({
            key: value ? [value] : selectedItem_?.id ? [selectedItem_.id] : [],
            mode: PaginationMode.Navigate,
          })
        : source({query: searchQuery});

    abortSignal.current = abort;

    return {
      abort: (): void => {
        aborted = true;
      },
      promise: (async (): Promise<void> => {
        const response = await promise;

        if (aborted) {
          return;
        }

        setItems(response.items);
        setHasMoreAfter(response.hasMoreAfter);
        setHasMoreBefore(response.hasMoreBefore);
      })(),
    };
  }, [searchQuery, source, selectedItem_, value]);

  const loadMoreAfter = useCallback(async (): Promise<void> => {
    onLoadMoreAfter?.();

    if (!source) {
      return;
    }
    abortSignal.current?.();

    const {abort, promise} = source({
      cursor: items[items.length - 1]?.cursor,
      direction: PaginationDirection.Forward,
      query: searchQuery,
    });

    abortSignal.current = abort;
    const response = await promise;
    const toBeItems = [...items, ...response.items];

    if (toBeItems.length > LIST_SIZE_LIMIT) {
      setHasMoreBefore(true);
    }

    setItems(toBeItems.slice(-LIST_SIZE_LIMIT));
    setHasMoreAfter(response.hasMoreAfter);
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, source]);

  const loadMoreBefore = useCallback(async (): Promise<void> => {
    onLoadMoreBefore?.();

    if (!source) {
      return;
    }
    abortSignal.current?.();

    const {abort, promise} = source({
      cursor: items[0]?.cursor,
      direction: PaginationDirection.Backward,
      query: searchQuery,
    });

    abortSignal.current = abort;
    const response = await promise;
    const toBeItems = [...response.items, ...items];

    if (toBeItems.length > LIST_SIZE_LIMIT) {
      setHasMoreAfter(true);
    }

    setItems(toBeItems.slice(0, LIST_SIZE_LIMIT));
    setHasMoreBefore(response.hasMoreBefore);
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, source]);

  useEffect(() => (): void => abortSignal.current?.(), []);

  const handleClick = useCallback(
    (event: any): void => {
      if (props.disabled) {
        return;
      }
      setExpanded(true);
      if (!dropdownRef.current || !dropdownRef.current.contains(event.target)) {
        setExpanded(true);
      }

      onClick?.(event);
    },
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggleSelectMenu, props.disabled]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (
        !event.relatedTarget ||
        !(event.relatedTarget instanceof HTMLElement) ||
        (!selectWrapperRef.current?.contains(event.relatedTarget) &&
          selectWrapperRef.current !== event.relatedTarget)
      ) {
        onFocusLose?.();
      }
      onBlur?.(event);
    },
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onBlur]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (props.disabled) {
        return;
      }
      const {key, target, currentTarget} = event;

      if (key === 'Enter' || key === ' ') {
        event.stopPropagation();
        if (
          (!clearIconRef.current ||
            (!clearIconRef.current.contains(target as Node) &&
              clearIconRef.current !== target)) &&
          (!searchRef.current ||
            (!searchRef.current.contains(target as Node) &&
              searchRef.current !== target))
        ) {
          event.preventDefault();
          if (
            !dropdownRef.current ||
            !dropdownRef.current.contains(currentTarget)
          ) {
            toggleSelectMenu();
          }
        }
      } else if (key === 'Backspace') {
        onClear?.();
      } else if (key === 'Escape') {
        if (expanded) {
          setExpanded(false);
          event.stopPropagation();
        }
      }

      onKeyDown?.(event);
    },
    [
      props.disabled,
      onKeyDown,
      toggleSelectMenu,
      onClear,
      expanded,
      setExpanded,
    ]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (props.disabled) {
        return;
      }

      if (event.key.length === 1 && event.key !== ' ') {
        if (!expanded && !props.disabled && searchable) {
          setExpanded(true);
          onSearchQueryChange?.(event.key);
          setLocalSearchValue(event.key);
          event.preventDefault();
        }
      }

      onKeyPress?.(event);
    },
    [
      props.disabled,
      onKeyPress,
      expanded,
      searchable,
      setExpanded,
      onSearchQueryChange,
    ]
  );

  const handleSearchBarKeyDown = useCallback(
    (event: any): void => {
      const {key} = event;

      if (key === 'ArrowDown') {
        if (filteredItems.length > 0 && listRef) {
          setSelection(({mainItems, suggestions}) => {
            const nextActiveItem = filteredItems.find(
              (item) =>
                item !== mainItems[0] &&
                (!item.id || item.id !== mainItems[0]?.id) &&
                !item.inactive
            );

            return {
              currentSection: 'main',
              mainItems: nextActiveItem ? [nextActiveItem] : mainItems,
              suggestions,
            };
          });
          setTimeout(() => listRef.focus(), 0);
        }
        event.stopPropagation();
        event.preventDefault();
      } else if (key === 'ArrowUp') {
        if (suggestedItems && suggestedItems.length > 0 && suggestionsListRef) {
          setSelection(({mainItems, suggestions}) => ({
            currentSection: 'suggestions',
            mainItems,
            suggestions,
          }));
          setTimeout(() => suggestionsListRef.focus(), 0);
        }
        event.stopPropagation();
      } else if (key === 'Enter' || key === ' ') {
        event.stopPropagation();
      } else if (key === 'Escape') {
        setExpanded(false);
        event.preventDefault();
      } else if (key === 'Backspace' || key === 'Delete') {
        event.stopPropagation();
      }
    },
    [filteredItems, listRef, setExpanded, suggestionsListRef, suggestedItems]
  );

  const handleListActiveItemChange = useCallback(
    (item: T | undefined): void => {
      if (props.disabled) {
        return;
      }
      if (item?.id != null) {
        onChange?.(item.id, undefined, item);
      }
      if (item) {
        onItemChange?.(item);
      }
      if (item && !Reflect.has(item, 'subMenuItems')) {
        setExpanded(false);
      }
    },
    [onChange, onItemChange, setExpanded, props.disabled]
  );

  const handleSearchBarKeyPress = useCallback(
    (event: any): void => {
      if (event.key === 'Enter') {
        if (onSearchQuerySubmit) {
          onSearchQuerySubmit(searchQuery);
          setExpanded(false);
        } else if (firstActiveItem) {
          handleListActiveItemChange(firstActiveItem);
        }
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [
      onSearchQuerySubmit,
      firstActiveItem,
      searchQuery,
      setExpanded,
      handleListActiveItemChange,
    ]
  );

  const handleListSelectionChange = useCallback(
    (selection_: T[]) => {
      if (selection_.length > 0) {
        setSelection(({suggestions}) => ({
          currentSection: 'main',
          mainItems: selection_,
          suggestions,
        }));
      } else if (searchable) {
        setSelection(({suggestions}) => ({
          currentSection: 'search',
          mainItems: firstActiveItem ? [firstActiveItem] : [],
          suggestions,
        }));
      }
    },
    [firstActiveItem, searchable]
  );

  useEffect(() => {
    if (
      selection.mainItems.length === 0 &&
      selection.currentSection === 'search'
    ) {
      setSelection(({suggestions}) => ({
        currentSection: 'search',
        mainItems: firstActiveItem ? [firstActiveItem] : [],
        suggestions,
      }));
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems]);

  const handleListTopTouch = useCallback((): void => {
    setSelection(({mainItems, suggestions}) => ({
      currentSection: 'search',
      mainItems,
      suggestions,
    }));
    focusSearchBar();
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuggestionsListBottomTouch = useCallback((): void => {
    setSelection(({mainItems, suggestions}) => ({
      currentSection: 'search',
      mainItems,
      suggestions,
    }));
    focusSearchBar();
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuggestionsListSelectionChange = useCallback(
    (selection_: T[]) => {
      if (selection_.length !== 0) {
        setSelection(({mainItems}) => ({
          currentSection: 'suggestions',
          mainItems,
          suggestions: selection_,
        }));
      }
    },
    []
  );

  const handleClearClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      event.stopPropagation();
      onClear?.();
    },
    [onClear]
  );

  const handleSearchQueryChange = useCallback(
    (query: any) => {
      onSearchQueryChange?.(query);
      setLocalSearchValue(query);
    },
    [onSearchQueryChange]
  );
  const WrapperComponent =
    startingPoint === StartingPoint.Input ? Wrapper : 'div';
  const HeaderComponent =
    startingPoint === StartingPoint.Input ? InputHeader : Header;

  const {popup: dropdown} = usePopup({
    content: (
      <Dropdown
        data-testid='selectbox:dropdown'
        empty={filteredItems.length === 0}
        hasSuggestedItems={suggestedItems != null}
        startingPoint={startingPoint}
      >
        {suggestedItems ? (
          <List
            ref={setSuggestionsListRef}
            data-testid='suggestions-list'
            ItemComponent={ItemComponent}
            items={suggestedItems}
            selectable={!disableSelect}
            selection={
              selection.currentSection === 'suggestions'
                ? selection.suggestions
                : []
            }
            activateOnFocus
            hoverSelectable
            jumpToActiveItem
            onActiveItemChange={handleListActiveItemChange}
            onBottomTouch={handleSuggestionsListBottomTouch}
            onSelectionChange={handleSuggestionsListSelectionChange}
          />
        ) : null}
        {searchable && (
          <SearchBar
            inputRef={searchRef}
            showSearchIcon={showSearchIcon}
            value={searchQuery}
            onChange={handleSearchQueryChange}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onKeyDown={handleSearchBarKeyDown}
            onKeyPress={handleSearchBarKeyPress}
          />
        )}
        {(source != null && itemsStatus === State.Loading) ||
        (source == null && isLoading) ? (
          <Loading noDelay />
        ) : hidePlaceholderWhenQueryIsEmpty &&
          filteredItems.length === 0 &&
          !searchQuery ? null : !source || itemsStatus === State.Success ? (
          <List
            ref={setListRef}
            activeItem={selectedItem}
            data-testid='main-list'
            hasMoreAfter={hasMoreAfter}
            hasMoreBefore={hasMoreBefore}
            highlight={searchQuery}
            ItemComponent={ItemComponent}
            items={filteredItems}
            jumpToActiveItem={!nonFocusActiveItem}
            nonFocusJumpActiveItem={nonFocusActiveItem}
            placeholderHeader={
              dropdownPlaceholderHeader == null
                ? searchQuery
                  ? t('selectbox.notFound.Message', {searchQuery})
                  : t('selectbox.notFound.emptyMessage')
                : dropdownPlaceholderHeader
            }
            PlaceholderImage={DropdownPlaceholderImage}
            sectionComponents={props.sectionComponents}
            selectable={!disableSelect}
            selection={selection.mainItems}
            title={title}
            activateOnFocus
            hoverSelectable
            onActiveItemChange={handleListActiveItemChange}
            onLoadMoreAfter={loadMoreAfter}
            onLoadMoreBefore={loadMoreBefore}
            onSelectionChange={handleListSelectionChange}
            onTopTouch={handleListTopTouch}
            {...listProps}
          />
        ) : (
          <div>
            {t('errors.loadingFailed')}
            <br />
            <button type='button' onClick={updateItems}>
              {t('actions.retry')}
            </button>
          </div>
        )}
      </Dropdown>
    ),
    disableDrag: true,
    forceAutoWidth: naturalDropdownWidth,
    frameElement,
    isVisible: expanded,
    mirrorOnOverflow: true,
    placement,
    disablePortal: true,
    target: baseReference?.current || internalRef,
    width: startingPoint === StartingPoint.Input ? internalRef : undefined,
    onClose: (evt: React.MouseEvent) => {
      evt.stopPropagation();
      setExpanded(false);
    },
  });

  return (
    <WrapperComponent
      ref={selectWrapperRef}
      aria-expanded={expanded}
      data-testid={testId}
      role='combobox'
      title={title}
      onBlur={handleBlur}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onKeyPress={handleKeyPress}
      {...props}
    >
      {startingPoint === StartingPoint.Input && label ? (
        <TopSection>
          <Label id={labelId}>{label}</Label>
          {title && (
            <ETooltip title={title}>
              <InfoIconWrapper>
                <Info />
              </InfoIconWrapper>
            </ETooltip>
          )}
        </TopSection>
      ) : null}
      {!noHeader && (
        <HeaderComponent
          ref={setInternalRef}
          aria-disabled={props.disabled}
          aria-labelledby={
            startingPoint === StartingPoint.Input ? labelId : undefined
          }
          disabled={props.disabled}
          error={error}
          success={success}
          tabIndex={
            props.disabled ? undefined : tabIndex || (expanded ? -1 : 0)
          }
          title={props.headerTitle}
        >
          {[StartingPoint.Dropdown, StartingPoint.Input].includes(
            startingPoint
          ) && (
            <>
              <HeaderLabel
                className='HeaderLabel'
                displayingPlaceholder={
                  !headerText && selectedItem?.text == null
                }
              >
                {allowInputAsItem
                  ? ''
                  : headerText
                    ? headerText
                    : selectedItem?.text == null
                      ? placeholder
                      : selectedItem.text}
              </HeaderLabel>
              <IconWrapper>
                {onClear && selectedItem ? (
                  <StyledClearButtonWrapper>
                    <span ref={clearIconRef}>
                      <EIconSmallButton onClick={handleClearClick}>
                        <Close />
                      </EIconSmallButton>
                    </span>
                  </StyledClearButtonWrapper>
                ) : null}
                <StartIcon />
              </IconWrapper>
            </>
          )}
          {startingPoint === StartingPoint.Icon && (
            <HeaderLabel className='HeaderLabel' displayingPlaceholder={false}>
              <StartIcon />
            </HeaderLabel>
          )}
        </HeaderComponent>
      )}
      {leftAssistiveText || actions.length > 0 ? (
        <BottomSection error={error} success={success}>
          <LeftAssistiveText>{leftAssistiveText}</LeftAssistiveText>
          {actions.map((action) => (
            <Action
              key={action.label}
              callback={action.callback}
              label={action.label}
            />
          ))}
        </BottomSection>
      ) : null}
      {dropdown}
    </WrapperComponent>
  );
}

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export default memo(SelectBoxComponent, StyledSelectBox, true);

export {StartingPoint, StartingPoint as SelectBoxStartingPoint};
